import { Direction } from '@prophecy/interfaces/generic';
import React from 'react';
import styled from 'styled-components';
const alignments: { [key: string]: string } = {
  stretch: 'stretch',
  start: 'flex-start',
  end: 'flex-end',
  baseline: 'baseline',
  center: 'center',
  'space-between': 'space-between',
  'space-around': 'space-around',
  'space-evenly': 'space-evenly'
};

export interface StackProps extends React.HTMLAttributes<HTMLDivElement> {
  direction?: Direction;
  height?: string;
  width?: string;
  align?: string;
  grow?: string;
  shrink?: string;
  alignY?: string;
  gap?: string;
  padding?: string;
  wrap?: string | boolean;
}

function gridStyles(props: StackProps) {
  const {
    direction = 'vertical',
    height = 'auto',
    width = 'auto',
    align = 'start',
    alignY = 'stretch',
    gap,
    padding = '0px'
  } = props;
  const _alignY = alignments[alignY];
  const _align = alignments[align];
  if (direction === 'vertical') {
    return `
      padding: ${padding};
      flex-direction:column;
      justify-content: ${_align};
      align-items: ${_alignY};
      gap: ${gap || '0px'};
      width:${width};
      height:${height};
    `;
  } else {
    return `
      padding: ${padding};
      justify-content: ${_align};
      align-items: ${_alignY};
      gap: ${gap || '0px'};
      width:${width};
      height:${height};
      `;
  }
}

export const Stack = styled.div.withConfig({
  forwardProps: ({
    direction: _direction,
    align: _align,
    grow: _grow,
    shrink: _shrink,
    alignY: _alignY,
    gap: _gap,
    padding: _padding,
    wrap: _wrap,
    width: _width,
    height: _height,
    ...rest
  }) => rest
})<StackProps>`
  display: flex;
  ${gridStyles};
  ${({ grow }) => (grow ? `flex-grow: ${grow};` : '')};
  ${({ shrink }) => (shrink ? `flex-shrink: ${shrink};` : '')};
  ${({ wrap }) => {
    if (typeof wrap === 'boolean' && wrap) {
      return 'flex-wrap: wrap';
    } else {
      return `flex-wrap: ${wrap}`;
    }
  }};
`;

export type StackItemProps = {
  gap?: string;
  grow?: string;
  shrink?: string;
  basis?: string;
  width?: string;
  height?: string;
  padding?: string;
  align?: string;
};

export const StackItem = styled.div.withConfig({
  forwardProps: ({
    gap: _gap,
    padding: _padding,
    basis: _basis,
    grow: _grow,
    shrink: _shrink,
    width: _width,
    height: _height,
    align: _align,
    ...rest
  }) => rest
})<StackItemProps>`
  /**
    * one drawback of flex, is even if flex 1 is provided, it doesn't shrink  beyond its content height. For it to work,
    * either we have to give min-height: 0; or min-width: 0 based on the direction.
    * So we are making it a default value on our StackItem . Though one side-effect is if two child are flex, then it will always respect flex value,
    * and ignore the content height/width which looks like an expected behaviour for us
    */
  min-height: 0;
  min-width: 0;
  ${(props) => (props.align ? `align-self: ${props.align}` : '')};
  ${(props) => (props.width ? `width: ${props.width}` : '')};
  ${(props) => (props.height ? `height: ${props.height}` : '')};
  padding: ${(props) => props.padding ?? props.gap ?? '0px'};
  flex-basis: ${(props) => props.basis ?? 'auto'};
  flex-grow: ${(props) => props.grow ?? '0'};
  flex-shrink: ${(props) => props.shrink ?? '1'}; /** Default value of flex-shrink is 1 */
`;
